import { ChevronLeft } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { lifespan, orbit, type Relation } from '../../../shared/tree'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { api, type Me } from '@/lib/api'
import type { CircleKind } from '@/lib/families'
import { useTree, type PersonFields } from '@/lib/tree'
import { AddConnection } from './AddConnection'
import { Avatar } from './Avatar'
import { Orbit } from './Orbit'
import { PersonForm } from './PersonForm'
import { PageLoader } from '@/components/Loader'

// The family tree as a walk through the family: one person's orbit at a time, starting with you. In a family circle
// it nudges you to build upward from yourself, starting with your parents; a casual circle skips the lineage prompts.
// onStart hears who an empty tree was started with.
export function FamilyTree({
  familyId,
  kind,
  focusId,
  me,
  onStart,
}: {
  familyId: string
  kind: CircleKind
  focusId?: string
  me: Me
  onStart?: (personId: string) => void
}) {
  const tree = useTree(familyId)
  const navigate = useNavigate()
  const [adding, setAdding] = useState<{ for: string; start: Relation | null } | null>(null)
  if (tree.isPending) return <PageLoader label="Loading the family tree" />
  if (tree.isError) return <FormError error={tree.error} />
  const t = tree.data
  if (t.people.length === 0) return <StartTree familyId={familyId} kind={kind} me={me} onStart={onStart} />

  const byId = new Map(t.people.map((p) => [p.id, p]))
  const focus = (focusId && byId.get(focusId)) || (t.me && byId.get(t.me)) || t.people[0]!
  const open = adding?.for === focus.id ? adding : null
  const askForParents = kind === 'family' && focus.id === t.me && orbit(focus.id, t.links).parents.length === 0
  const everyone = [...t.people].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <section aria-label="Family tree" className="flex flex-col gap-8">
      {t.me && focus.id !== t.me && (
        // After walking a few people away, one tap brings you home.
        <Link
          to={`/circle/${familyId}/people/${t.me}`}
          className="-mb-5 flex h-11 items-center gap-1 self-start rounded-full pr-3.5 pl-2 text-sm font-medium text-muted-foreground outline-none hover:bg-secondary hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:h-9"
        >
          <ChevronLeft className="size-4" aria-hidden />
          Back to you
        </Link>
      )}
      <Orbit familyId={familyId} tree={t} focus={focus}>
        <Button
          size="sm"
          variant={open ? 'secondary' : 'outline'}
          aria-expanded={!!open}
          onClick={() => setAdding(open ? null : { for: focus.id, start: null })}
        >
          Add a family connection
        </Button>
      </Orbit>

      {askForParents && !open && (
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="whisper">Every tree grows from somewhere. Start with your parents.</p>
          <Button onClick={() => setAdding({ for: focus.id, start: 'parent' })}>Add your parents</Button>
        </div>
      )}

      {open && (
        <div className="rounded-3xl bg-secondary/70 p-5 sm:p-6">
          <AddConnection
            key={open.start ?? ''}
            familyId={familyId}
            of={focus}
            tree={t}
            start={open.start}
            onDone={() => setAdding(null)}
          />
        </div>
      )}

      {!t.me && (
        <AddYourself familyId={familyId} me={me} onAdded={(id) => navigate(`/circle/${familyId}/people/${id}`)} />
      )}

      <details className="group rounded-3xl border border-border px-5 py-3">
        <summary className="flex min-h-10 cursor-pointer items-center justify-between font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
          Everyone in the tree <span className="text-sub text-muted-foreground">{t.people.length}</span>
        </summary>
        <ul className="mt-2 flex flex-col">
          {everyone.map((p) => (
            <li key={p.id}>
              <Link
                to={`/circle/${familyId}/people/${p.id}`}
                aria-current={p.id === focus.id ? 'true' : undefined}
                className="flex min-h-12 items-center gap-3 rounded-2xl px-2 outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-[current]:bg-secondary"
              >
                <Avatar person={p} className="size-8 text-sm" />
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                <span className="text-sub text-muted-foreground">{lifespan(p)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </details>
    </section>
  )
}

// An empty tree starts with the archivist.
function StartTree({
  familyId,
  kind,
  me,
  onStart,
}: {
  familyId: string
  kind: CircleKind
  me: Me
  onStart?: (personId: string) => void
}) {
  const queryClient = useQueryClient()
  const [other, setOther] = useState(false)
  const add = useMutation({
    mutationFn: (body: Partial<PersonFields> & { me?: boolean }) =>
      api<{ id: string }>(`/families/${familyId}/people`, { method: 'POST', body }),
    onSuccess: async ({ id }) => {
      await queryClient.invalidateQueries({ queryKey: ['tree', familyId] })
      onStart?.(id)
    },
  })
  return (
    <section aria-label="Family tree" className="flex flex-col gap-5">
      <p className="text-muted-foreground">
        {kind === 'family'
          ? 'Start the tree with yourself, then add your people around you: parents first, then partner, children, brothers and sisters, and the friends who are family.'
          : 'Start with yourself, then add the people in this circle and how they’re connected.'}
      </p>
      <FormError error={add.error} />
      {!other ? (
        <div className="flex flex-wrap gap-2">
          <Button disabled={add.isPending} onClick={() => add.mutate({ name: me.name, me: true })}>
            Start with me
          </Button>
          <Button variant="outline" onClick={() => setOther(true)}>
            Start with someone else
          </Button>
        </div>
      ) : (
        <PersonForm label="Add to the tree" pending={add.isPending} error={null} onSave={(f) => add.mutate(f)} />
      )}
    </section>
  )
}

// When the tree was started by someone else and you're not on it yet.
function AddYourself({ familyId, me, onAdded }: { familyId: string; me: Me; onAdded: (id: string) => void }) {
  const queryClient = useQueryClient()
  const add = useMutation({
    mutationFn: () =>
      api<{ id: string }>(`/families/${familyId}/people`, { method: 'POST', body: { name: me.name, me: true } }),
    onSuccess: async ({ id }) => {
      await queryClient.invalidateQueries({ queryKey: ['tree', familyId] })
      onAdded(id)
    },
  })
  return (
    <div className="flex flex-col gap-2 rounded-3xl bg-secondary/70 p-5">
      <p>
        You’re not in this tree yet. If someone already added you, open your page and tap “This is me”. Otherwise, add
        yourself.
      </p>
      <FormError error={add.error} />
      <Button variant="outline" className="self-start" disabled={add.isPending} onClick={() => add.mutate()}>
        Add me to the tree
      </Button>
    </div>
  )
}
